import {
    createExpressionContext,
    evaluateExpressionSafe,
} from "./expression-sandbox.js";
import { applyNumberPrecision, normalizeComputed } from "./compute-value.js";
import { checkComputedValueType, formatTypeMismatch } from "./computed-type-check.js";
import { buildItemTypeMap } from "./form-helpers.js";
import { EXPRESSION_SOURCE } from "./design-fault-reporting.js";
import { METHOD_TYPE } from "./method-types.js";
import { logTypeMismatch } from "./type-mismatch-reporting.js";

/**
 * Returns true when the item value is considered "empty" and a default should
 * be applied.  Mirrors the default-value logic used by form-visibility.js:
 * - null / undefined / "" → empty
 * - [] (Checkboxes with no selection) → empty
 * - 0, false, and any other truthy/falsy scalar → NOT empty (valid user input)
 */
function isEmpty(value) {
    if (value === null || value === undefined || value === "") {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

/**
 * Applies `MethodDef` Imputation defaults to the form's items.
 *
 * This is `useDefaults`' effect body, lifted out unchanged so it can run — and
 * be tested — without a React component tree, and so it can live in the engine
 * half of the shared package. The hook keeps only the scheduling: the read-only
 * check, owning the `appliedOids` Set and resetting it when the form definition
 * changes, and holding the callback in a ref.
 *
 * Everything the effect did is still done here, in the same order, through
 * injected callbacks rather than returned as a plan. That is deliberate: a plan
 * would have batched all the mismatch reports before all the writes, and while
 * no current test detects the difference, "the same operations in the same
 * order" is a much easier property to be sure of than "an equivalent plan".
 * Elegance can come later; this move is meant to be provably behaviour-neutral.
 *
 * For the same reason `appliedOids` is mutated in place, exactly as the hook's
 * ref was: an item is added once the pass reaches a verdict for it — wrote a
 * value, found existing input, or produced something unusable — so a later
 * render does not re-fire it. It is deliberately NOT added when the expression
 * yielded nothing, because a dependency it needs may still be arriving.
 *
 * @param {{
 *   form: object,
 *   appliedOids: Set<string>,  mutated: gains every item settled by this pass
 *   setValue: (oid: string, value: *) => void,
 *   setItemValue: (oid: string, value: *, format?: *) => void,
 *   onTypeMismatch?: (mismatch: object) => void,
 * }} params
 */
export function applyImputationDefaults({
    form,
    appliedOids,
    setValue,
    setItemValue,
    onTypeMismatch,
}) {
    if (!form?.itemGroups?.length) {
        return;
    }

    const context = createExpressionContext({
        formData: form.formData ?? {},
        dependencies: form.dependencies ?? {},
        identifierModel: form.identifierModel ?? {},
        itemTypes: buildItemTypeMap(form),
    });

    for (const itemGroup of form.itemGroups) {
        for (const item of itemGroup.items ?? []) {
            const { methodDef, oid } = item;

            if (!methodDef || methodDef.methodType !== METHOD_TYPE.Imputation) {
                continue;
            }
            // Skip the default-placeholder MethodDef (methodType=1, expression=null)
            // that Format.Cdisc 2.20.0 emits for every item with no ImputationMethodOID.
            if (!methodDef.expression) {
                continue;
            }
            // Hidden items: leave for a later render. updateVisibility
            // sets isVisible on each item; an undefined value means
            // "no visibility tracked yet" (test fixture / pre-render),
            // which we treat as visible so the existing test surface
            // keeps working. Hide-always items stay skipped too, unlike
            // in the recompute hook: the server never executes Imputation
            // methods, so there is no background default to mirror.
            if (item.isVisible === false) {
                continue;
            }
            if (appliedOids.has(oid)) {
                continue;
            }

            const currentValue = form.formData?.[oid];
            if (!isEmpty(currentValue)) {
                // Pre-existing value (user typed, server-supplied, or
                // we already applied earlier in this render pass).
                // Lock the item so we don't re-fire if the user later
                // clears the field.
                appliedOids.add(oid);
                continue;
            }

            // The fourth argument is provenance for the design-fault sink, so the
            // preview panel can attribute a failing expression to this item. It is
            // a no-op outside the preview, which means dropping it here would be
            // invisible to every test and to the parity harness -- see the origin
            // test in imputation-defaults.test.js.
            const computed = evaluateExpressionSafe(
                methodDef.expression,
                context,
                undefined,
                {
                    source: EXPRESSION_SOURCE.DEFAULT_VALUE,
                    oid,
                    label: item.label,
                }
            );

            // Expression couldn't yield a value (e.g. sidebar dep still
            // empty). Don't lock — try again next render once the deps
            // arrive.
            if (computed === undefined) {
                continue;
            }

            // normalizeComputed maps NaN/±Infinity (and undefined) to
            // undefined, so a non-finite default is skipped rather than
            // written as garbage. An invalid Date also maps to undefined
            // there, because date-fns format() throws on one and this call
            // is not wrapped.
            const normalized = normalizeComputed(computed);

            // Type gate, same rule set as useFunctionRecompute. An
            // Imputation default is the ONLY line of defence for its item:
            // FormDataSaveService.ExecuteFunctionsAsync re-executes
            // computations server-side but selects on MethodOID, and ODM
            // carries defaults under ImputationMethodOID — so a bad default
            // is never corrected later.
            //
            // An invalid Date is the one case where the raw result says
            // more than the normalized one (see above), so the gate is
            // given the raw value there.
            const isInvalidDate =
                computed instanceof Date && Number.isNaN(computed.getTime());
            const typeCheck = checkComputedValueType(
                isInvalidDate ? computed : normalized,
                item.itemType
            );

            if (!typeCheck.ok) {
                const mismatch = {
                    oid,
                    label: item.label,
                    itemType: item.itemType,
                    expression: methodDef.expression,
                    expected: typeCheck.expected,
                    actual: typeCheck.actual,
                    source: "default",
                };
                const message = formatTypeMismatch(mismatch);
                onTypeMismatch?.({ ...mismatch, message });
                // No dedupe needed, unlike the recompute path: a default
                // fires at most once per form definition because of the
                // lock below, so there is no per-keystroke repeat to
                // suppress.
                logTypeMismatch(mismatch, message);

                // Do NOT write. Unlike a Computation there is nothing to
                // clear — the item is empty by definition here (isEmpty
                // above), so leaving it alone IS the correct outcome.
                //
                // Locked anyway: the expression produced a value, just an
                // unusable one, so retrying every render would only repeat
                // the report. Matches the "produced something -> lock"
                // half of the contract above.
                appliedOids.add(oid);
                continue;
            }

            if (normalized !== undefined) {
                // Same Number-decimals treatment as useFunctionRecompute:
                // round the default to the designer decimals and store the
                // setting as the __format sidecar.
                const { value: storedValue, format } = applyNumberPrecision(
                    normalized,
                    item
                );
                setValue(oid, storedValue);
                setItemValue(oid, storedValue, format);
            }

            appliedOids.add(oid);
        }
    }
}
